import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ProductDetail } from './pages/ProductDetail';
import { Shop } from './pages/Shop';
import { HomePage } from './pages/HomePage';
import { Cart } from './pages/Cart';
import { Booking } from './pages/Booking';
import { TrackOrder } from './pages/TrackOrder';
import { Warranty } from './pages/Warranty';
import { FAQ } from './pages/FAQ';
import { ShippingInformation } from './pages/ShippingInformation';
import { CartProvider } from './context/CartContext';
import { AdminLogin } from './pages/admin/Login';
import { ForgotPassword } from './pages/admin/ForgotPassword';
import { AdminDashboard } from './pages/admin/Dashboard';
import { DashboardHome } from './pages/admin/DashboardHome';
import { AdminInventory } from './pages/admin/Inventory';
import { AdminNewProduct } from './pages/admin/NewProduct';
import { Analytics } from './pages/admin/Analytics';
import { Orders } from './pages/admin/Orders';
import { Calendar } from './pages/admin/Calendar';
import { CustomerSupport } from './pages/admin/CustomerSupport';
import { AdminRoute } from './components/admin/AdminRoute';
import { ToastProvider } from './context/ToastContext';
import { NotificationProvider } from './context/NotificationContext';
import { ChatBot } from './components/ChatBot';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { SiteSEO } from './components/SEO';
import { MobileActionBar } from './components/MobileActionBar';
import { trackEvent, trackPageView } from './utils/analytics';

// ScrollToTop component to handle scroll restoration
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const clickable = target?.closest('a, button');

      if (!clickable) return;

      const href = clickable.getAttribute('href') || '';
      const label = (
        clickable.getAttribute('aria-label') ||
        clickable.textContent ||
        'Unlabeled click'
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);
      const normalizedLabel = label.toLowerCase();
      const pagePath = `${window.location.pathname}${window.location.search}`;

      trackEvent('site_click', {
        click_label: label,
        click_url: href,
        page_path: pagePath,
      });

      if (href.startsWith('tel:') || normalizedLabel.includes('call now')) {
        trackEvent('call_click', { page_path: pagePath });
      }

      if (href.startsWith('sms:') || normalizedLabel.includes('text us')) {
        trackEvent('text_click', { page_path: pagePath });
      }

      if (normalizedLabel.includes('add to cart')) {
        trackEvent('add_to_cart_click', { page_path: pagePath });
      }

      if (normalizedLabel.includes('book installation')) {
        trackEvent('book_installation_click', { page_path: pagePath });
      }

      if (href === '/cart' || normalizedLabel.includes('view cart')) {
        trackEvent('view_cart_click', { page_path: pagePath });
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return null;
}

function App() {
  return (
    <ToastProvider>
      <CartProvider>
        <NotificationProvider>
          <Router>
            <ScrollToTop />
            <AnalyticsTracker />
            <SiteSEO />
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Routes>
                {/* Admin Routes - No Navigation */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<ForgotPassword />} />
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route
                  path="/admin/*"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                >
                  <Route path="dashboard" element={<DashboardHome />} />
                  <Route path="inventory" element={<AdminInventory />} />
                  <Route path="inventory/new" element={<AdminNewProduct />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="calendar" element={<Calendar />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="support" element={<CustomerSupport />} />
                </Route>

                {/* Public Routes - With Navigation */}
                <Route
                  path="*"
                  element={
                    <div className="flex min-h-screen flex-col pb-16 md:pb-0">
                      <Navigation />
                      <main className="flex-1">
                        <Routes>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/shop" element={<Shop />} />
                          <Route path="/product/:id" element={<ProductDetail />} />
                          <Route path="/cart" element={<Cart />} />
                          <Route path="/booking" element={<Booking />} />
                          <Route path="/track-order" element={<TrackOrder />} />
                          <Route path="/warranty" element={<Warranty />} />
                          <Route path="/faq" element={<FAQ />} />
                          <Route path="/shipping-information" element={<ShippingInformation />} />
                        </Routes>
                      </main>
                      <Footer />
                      <ChatBot />
                      <MobileActionBar />
                    </div>
                  }
                />
              </Routes>
            </div>
          </Router>
        </NotificationProvider>
      </CartProvider>
    </ToastProvider>
  );
}

export default App;
